/**
 * Text Style Tokens
 *
 * Pre-configured typography presets that bundle fontSize, lineHeight, and fontWeight.
 * Use these for consistent text styling across the application.
 *
 * Usage:
 *   <Text textStyle="body.md">Regular body text</Text>
 *   <Text textStyle="heading.lg">Large heading</Text>
 *   <Text textStyle="label.sm">Small label</Text>
 *
 * @see DESIGN_TOKENS.md for usage guidelines
 */

import { defineTextStyles } from "@chakra-ui/react";

// =============================================================================
// FONT SIZE PRIMITIVES
// =============================================================================

export const fontSizes = {
  "2xs": "0.625rem", // 10px
  xs: "0.75rem", // 12px
  sm: "0.875rem", // 14px
  md: "1rem", // 16px
  lg: "1.125rem", // 18px
  xl: "1.25rem", // 20px
  "2xl": "1.375rem", // 22px
  "3xl": "1.5rem", // 24px
  "4xl": "1.75rem", // 28px
  "5xl": "2.25rem", // 36px
  "5.5xl": "2.5rem", // 40px
  "6xl": "3rem", // 48px
} as const;

// =============================================================================
// LINE HEIGHT PRIMITIVES
// =============================================================================

export const lineHeights = {
  none: "1",
  tight: "1.25",
  snug: "1.375",
  normal: "1.5",
  relaxed: "1.625",
  loose: "2",
  // Specific pixel values for design system alignment
  "16": "1rem", // 16px
  "20": "1.25rem", // 20px
  "24": "1.5rem", // 24px
  "28": "1.75rem", // 28px
  "30": "1.875rem", // 30px
  "32": "2rem", // 32px
  "36": "2.25rem", // 36px
  "40": "2.5rem", // 40px
} as const;

// =============================================================================
// FONT WEIGHT PRIMITIVES
// =============================================================================

export const fontWeights = {
  hairline: "100",
  thin: "200",
  light: "300",
  normal: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
  extrabold: "800",
  black: "900",
} as const;

// =============================================================================
// TEXT STYLES - Chakra v3 format
// =============================================================================

export const textStyles = defineTextStyles({
  // Body text variants
  "body.2xs": {
    value: {
      fontSize: fontSizes["2xs"],
      lineHeight: lineHeights["16"],
      fontWeight: fontWeights.normal,
    },
  },
  "body.xs": {
    value: {
      fontSize: fontSizes.xs,
      lineHeight: lineHeights["16"],
      fontWeight: fontWeights.normal,
    },
  },
  "body.sm": {
    value: {
      fontSize: fontSizes.sm,
      lineHeight: lineHeights["20"],
      fontWeight: fontWeights.normal,
    },
  },
  "body.md": {
    value: {
      fontSize: fontSizes.md,
      lineHeight: lineHeights["24"],
      fontWeight: fontWeights.normal,
    },
  },
  "body.lg": {
    value: {
      fontSize: fontSizes.lg,
      lineHeight: lineHeights["28"],
      fontWeight: fontWeights.normal,
    },
  },
  "body.xl": {
    value: {
      fontSize: fontSizes.xl,
      lineHeight: lineHeights["30"],
      fontWeight: fontWeights.normal,
    },
  },

  // Heading variants
  "heading.xs": {
    value: {
      fontSize: fontSizes.xs,
      lineHeight: lineHeights["16"],
      fontWeight: fontWeights.semibold,
    },
  },
  "heading.sm": {
    value: {
      fontSize: fontSizes.sm,
      lineHeight: lineHeights["20"],
      fontWeight: fontWeights.semibold,
    },
  },
  "heading.md": {
    value: {
      fontSize: fontSizes.md,
      lineHeight: lineHeights["24"],
      fontWeight: fontWeights.semibold,
    },
  },
  "heading.lg": {
    value: {
      fontSize: fontSizes.lg,
      lineHeight: lineHeights["28"],
      fontWeight: fontWeights.semibold,
    },
  },
  "heading.xl": {
    value: {
      fontSize: fontSizes.xl,
      lineHeight: lineHeights["30"],
      fontWeight: fontWeights.bold,
    },
  },
  "heading.2xl": {
    value: {
      fontSize: fontSizes["2xl"],
      lineHeight: lineHeights["32"],
      fontWeight: fontWeights.bold,
    },
  },
  "heading.3xl": {
    value: {
      fontSize: fontSizes["3xl"],
      lineHeight: lineHeights["32"],
      fontWeight: fontWeights.bold,
    },
  },
  "heading.4xl": {
    value: {
      fontSize: fontSizes["4xl"],
      lineHeight: lineHeights["36"],
      fontWeight: fontWeights.bold,
    },
  },
  "heading.5xl": {
    value: {
      fontSize: fontSizes["5xl"],
      lineHeight: lineHeights["40"],
      fontWeight: fontWeights.bold,
    },
  },

  // Label variants (for form labels, badges, etc.)
  "label.xs": {
    value: {
      fontSize: fontSizes.xs,
      lineHeight: lineHeights["16"],
      fontWeight: fontWeights.medium,
    },
  },
  "label.sm": {
    value: {
      fontSize: fontSizes.sm,
      lineHeight: lineHeights["20"],
      fontWeight: fontWeights.medium,
    },
  },
  "label.md": {
    value: {
      fontSize: fontSizes.md,
      lineHeight: lineHeights["24"],
      fontWeight: fontWeights.medium,
    },
  },

  // Caption/meta variants (for timestamps, secondary info)
  "caption.xs": {
    value: {
      fontSize: fontSizes["2xs"],
      lineHeight: lineHeights["16"],
      fontWeight: fontWeights.normal,
    },
  },
  "caption.sm": {
    value: {
      fontSize: fontSizes.xs,
      lineHeight: lineHeights["16"],
      fontWeight: fontWeights.normal,
    },
  },
});

// =============================================================================
// FONT SIZE TOKENS - Chakra v3 format
// =============================================================================

export const fontSizeTokens = Object.entries(fontSizes).reduce(
  (acc, [key, val]) => {
    acc[key] = { value: val };
    return acc;
  },
  {} as Record<string, { value: string }>
);

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type FontSizeToken = keyof typeof fontSizes;
export type TextStyleToken = keyof typeof textStyles;
export type LineHeightToken = keyof typeof lineHeights;
export type FontWeightToken = keyof typeof fontWeights;
